import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import https from '../../utils/https';

export interface Shift {
    _id: string;
    name: string;
    startTime: string;
    endTime: string;
    status: "active" | "inactive";
    createdAt: string;
}

interface ShiftState {
    shifts: Shift[];
    selectedShift: Shift | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShiftState = {
    shifts: [],
    selectedShift: null,
    loading: false,
    error: null,
};

// Async Thunks
export const fetchShifts = createAsyncThunk('shift/fetchShifts', async (_, { rejectWithValue }) => {
    try {
        const response = await https.get('/orders/shift');
        // Handle response where data might be directly the array or wrapped in a data property
        return response.data || [];
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch shifts');
    }
});

export const fetchShiftById = createAsyncThunk('shift/fetchShiftById', async (id: string, { rejectWithValue }) => {
    try {
        const response = await https.get(`/orders/shift/${id}`);
        return response.data || response;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch shift details');
    }
});

export const addShift = createAsyncThunk('shift/addShift', async (shiftData: Omit<Shift, '_id' | 'createdAt'>, { rejectWithValue }) => {
    try {
        const response = await https.post('/orders/shift', shiftData);
        // Assuming the API returns the created shift object
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to add shift');
    }
});

export const updateShift = createAsyncThunk('shift/updateShift', async ({ id, data }: { id: string; data: Partial<Shift> }, { rejectWithValue }) => {
    try {
        const response = await https.put(`/orders/shift/${id}`, data);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to update shift');
    }
});

export const deleteShift = createAsyncThunk('shift/deleteShift', async (id: string, { rejectWithValue }) => {
    try {
        await https.delete(`/orders/shift/${id}`);
        return id;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to delete shift');
    }
});

const shiftSlice = createSlice({
    name: 'shift',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSelectedShift: (state) => {
            state.selectedShift = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch Shifts
        builder
            .addCase(fetchShifts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShifts.fulfilled, (state, action: PayloadAction<Shift[]>) => {
                state.loading = false;
                state.shifts = action.payload;
            })
            .addCase(fetchShifts.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch shifts';
            });

        // Fetch Shift By ID
        builder
            .addCase(fetchShiftById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShiftById.fulfilled, (state, action: PayloadAction<Shift>) => {
                state.loading = false;
                state.selectedShift = action.payload;
            })
            .addCase(fetchShiftById.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch shift details';
            });

        // Add Shift
        builder
            .addCase(addShift.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addShift.fulfilled, (state, action: PayloadAction<Shift>) => {
                state.loading = false;
                state.shifts.push(action.payload);
            })
            .addCase(addShift.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to add shift';
            });

        // Update Shift
        builder
            .addCase(updateShift.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShift.fulfilled, (state, action: PayloadAction<Shift>) => {
                state.loading = false;
                const index = state.shifts.findIndex(s => s._id === action.payload._id);
                if (index !== -1) {
                    state.shifts[index] = action.payload;
                }
                if (state.selectedShift && state.selectedShift._id === action.payload._id) {
                    state.selectedShift = action.payload;
                }
            })
            .addCase(updateShift.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update shift';
            });

        // Delete Shift
        builder
            .addCase(deleteShift.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteShift.fulfilled, (state, action: PayloadAction<string>) => {
                state.loading = false;
                state.shifts = state.shifts.filter(s => s._id !== action.payload);
                if (state.selectedShift && state.selectedShift._id === action.payload) {
                    state.selectedShift = null;
                }
            })
            .addCase(deleteShift.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete shift';
            });
    },
});

export const { clearError, clearSelectedShift } = shiftSlice.actions;
export default shiftSlice.reducer;
